import * as Sentry from '@sentry/browser';
import swal from 'sweetalert2';
import randomstring from 'randomstring';
import moment from 'moment-timezone';
import isArray from 'lodash/isArray';
import each from 'lodash/each';
import set from 'lodash/set';
import startCase from 'lodash/startCase';
import storage from 'viralstyle-ui/src/storage';
import Vue from 'vue';

import config from 'config';
import mixins from 'utils/client-mixins';
import CustomVar from './model';
import { RoleInfo, PersistentFilters } from './model';

export const state = {
  navMenuOpen: false,
  currentUser: null,
  currentUserId: null,
  currentUserRoleInfo: new RoleInfo(),
  currentUserEmail: null,
  intercomId: null,
  intercomHash: null,
  loginVisible: false,
  cobrandLogo: null,
  sessionSelectedDate: null,
  showMyOrdersLink: false,
  emailVerificationRequested: false,
  previousPersistableDate: null,
  persistentFilters: null,
  isImpersonation: false,
  logoutImpersonation: null,
  emailProviders: {
    AWEBER: 'Aweber',
    CONSTANTCONTACT: 'Constant Contact',
    GETRESPONSE: 'GetResponse',
    MAILCHIMP: 'MailChimp',
    KLAVIYO: 'Klaviyo',
  },
};

export const mutations = {
  setCurrentUserVariables(state, userData) {
    state.persistentFilters = new PersistentFilters(userData);

    if (userData.is_impersonation) {
      state.isImpersonation = userData.is_impersonation;
      state.logoutImpersonation = userData._links.end_impersonation_session.href;
    }

    if (userData.parent_user) {
      storage.set('client-hash', userData.parent_user.id);

      mutations.setCurrentUser(state, userData.parent_user);

      Vue.set(state.currentUser, 'isRoot', false);
    } else {
      storage.set('client-hash', userData.id);
    }

    state.currentUserId = userData.id;
    state.currentUserRoleInfo = new RoleInfo(userData.role, userData.permissions, userData.parent_user);
    state.currentUserEmail = userData.email;
    state.cobrandLogo = userData.cobrand_logo;
    state.intercomId = userData.intercom_id;
    state.intercomHash = userData.intercom_hash;

    if (userData.suspended) {
      try {
        swal({
          title: 'Account Suspended',
          type: 'info',
          text: 'Your account has been suspended for violating terms of service. For more information contact clientsupport@viralstyle.com.',
          confirmButtonColor: config.PRIMARY_COLOR,
        });
      } catch (e) {}
    }
  },

  setCurrentUser(state, userData) {
    userData.password = '';
    userData.newPassword = '';
    userData.confirmPassword = '';

    state.currentUser = userData;

    if (!Object.keys(state.currentUser.pixels.facebook.custom_vars).length) {
      Vue.set(state.currentUser.pixels.facebook, 'custom_vars', {})
    }

    if (isArray(state.currentUser.pixels.adroll)) {
      Vue.set(state.currentUser.pixels, 'adroll', {
        pixel_id: null,
        adv_id: null,
      });
    }

    let custom_vars = state.currentUser.pixels.facebook.custom_vars;

    each(custom_vars, customVars => each(customVars, type => type.symbol = randomstring.generate()));

    mutations.initVarType(custom_vars, 'VIEW_CONTENT');
    mutations.initVarType(custom_vars, 'ADD_TO_CART');
    mutations.initVarType(custom_vars, 'INITIATE_CHECKOUT');
    mutations.initVarType(custom_vars, 'PURCHASE');

    // raven user info
    if (config.RAVEN_URL) {
      Sentry.setUser({ email: userData.email, role: userData.role });
    }

    let filters = state.persistentFilters.filters,
      collection = state.campaigns.campaigns.all,
      dateRange = filters.date_range,
      isPresetDate = ['today', 'yesterday', 'last 7 days', 'last 30 days', 'all time', ].includes(dateRange),
      dateObject = { type: isPresetDate ? startCase(dateRange) : 'Custom', },
      dates;

    state.campaigns.displayType = filters.display_type;
    collection.sortFilter = filters.sort_column || 'campaigns.original_campaign_id:DESC';
    collection.statusFilter = filters.status || 'All';
    collection.productFilter = filters.product_type_id || -1;
    collection.domainFilter = filters.domain_id || -1;
    collection.createdByFilter = filters.created_by_id || -1;

    if (isPresetDate) {
      dates = mixins.methods.datesFromSelection(dateObject.type, state.currentUser.timezone);

      dateObject.start = dates.start;
      dateObject.end = dates.end;
    } else {
      dates = dateRange.split(',');

      if (dates[0].indexOf('/') !== -1) {
        dateObject.start = moment.tz(dates[0], 'M/D/YY', state.currentUser.timezone).startOf('day');
        dateObject.end = moment.tz(dates[1], 'M/D/YY', state.currentUser.timezone).endOf('day');
      } else {
        dateObject.start = moment.tz(dates[0], state.currentUser.timezone);
        dateObject.end = moment.tz(dates[1], state.currentUser.timezone);
      }
    }

    state.sessionSelectedDate = dateObject;
    collection.start = dateObject.start;
    collection.end = dateObject.end;
    collection.dateType = dateObject.type;
    state.previousPersistableDate = dateObject;
  },

  resetPersistentFilters(state) {
    let filters = state.persistentFilters.filters,
      collection = state.campaigns.campaigns.all,
      dateObject = { type: 'Today' },
      dates;

    state.campaigns.displayType = 'list';
    collection.sortFilter = 'campaigns.original_campaign_id:DESC';
    collection.statusFilter = 'All';
    collection.productFilter = -1;
    collection.domainFilter = -1;
    collection.createdByFilter = -1;
    collection.relaunchOption = 'All Time';

    dates = mixins.methods.datesFromSelection('Today', state.currentUser.timezone);
    dateObject.start = dates.start;
    dateObject.end = dates.end;

    state.sessionSelectedDate = dateObject;
    collection.start = dateObject.start;
    collection.end = dateObject.end;
    collection.dateType = dateObject.type;
  },

  initVarType(vars, type) {
    vars[type] ? vars[type].push(new CustomVar(type)) : Vue.set(vars, type, [new CustomVar(type)]);
  },

  updateCurrentUser(state, { property, value }) {
    set(state.currentUser, property, value);
  },

  toggleLogin(state, isVisible) {
    state.loginVisible = isVisible;
  },

  clearCurrentUserPassword(state) {
    set(state.currentUser, 'password', '');
    set(state.currentUser, 'newPassword', '');
    set(state.currentUser, 'confirmPassword', '');
    Vue.set(state.currentUser, state.currentUser);
  },

  removeFacebookCustomVar(state, { index, type }) {
    state.currentUser.pixels.facebook.custom_vars[type].splice(index, 1);
  },

  updateFacebookCustomVar(state, { index, property, value, type }) {
    state.currentUser.pixels.facebook.custom_vars[type][index][property] = value;

    let customVars = state.currentUser.pixels.facebook.custom_vars[type],
      customVar = customVars[index],
      lastVar = customVars[customVars.length - 1],
      previousVar;

    if (customVars.length > 1) previousVar = customVars[customVars.length - 2];

    // if: add new custom var if last var has key and value
    // else if: remove last custom var if previous var has key or value removed
    if (lastVar.key && lastVar.value) {
      customVars.push(new CustomVar(type));
    } else if (previousVar && (!previousVar.key || !previousVar.value)) {
      customVars.pop();
    }

    // remove custom var if its not the last one and its empty
    if (index !== customVars.length - 1 && !customVar.key && !customVar.value) {
      customVars.splice(index, 1);
    }
  },

  toggleNavMenu(state, visible) {
    state.navMenuOpen = visible;
  },

  setPaginatableCollection(state, { paginatableCollection, data }) {
    paginatableCollection.collection = [...paginatableCollection.collection, ...data._embedded.items];
    paginatableCollection.initialized = true;
    paginatableCollection.total = data.total_results;
    paginatableCollection._links = data._links;

    if (data._embedded.items.length === paginatableCollection.limit) {
      paginatableCollection.page++;
      paginatableCollection.canLoad = true;
    } else {
      paginatableCollection.canLoad = false;
    }
  },

  resetPaginatableCollection(state, paginatableCollection) {
    paginatableCollection.collection = [];
    paginatableCollection.page = 0;
    paginatableCollection.total = 0;
    paginatableCollection.canLoad = true;
    paginatableCollection.initialized = false;
  },
}